import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Sun, Moon, Menu, X, ShoppingCart, LayoutGrid, Shield, LogOut, Heart } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { useFavorites } from "../context/FavoritesContext";
import { useTheme } from "../App";
import { useLang } from "../context/LanguageContext";
import { LangToggle } from "./LangToggle";
import { AccessCountdown } from "./AccessCountdown";

export default function Header() {
  const { user, logout, settings } = useAuth();
  const { count } = useCart();
  const { count: favCount } = useFavorites();
  const theme = useTheme();
  const { t } = useLang();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  if (location.pathname === "/login" || location.pathname === "/") return null;

  const links = [
    { to: "/catalogo", label: t("nav.catalog"), icon: LayoutGrid, testid: "nav-catalog-link" },
    { to: "/catalogo?fav=1", label: t("nav.favorites"), icon: Heart, badge: favCount, testid: "nav-favorites-link", badgeTestid: "favorites-count-badge" },
  ];
  if (user?.role !== "staff") links.push({ to: "/carrito", label: t("nav.cart"), icon: ShoppingCart, badge: count, testid: "nav-cart-link", badgeTestid: "cart-count-badge" });
  if (["admin", "staff"].includes(user?.role)) links.push({ to: "/admin", label: t("nav.admin"), icon: Shield, testid: "nav-admin-link" });

  const isActive = (to) => {
    const [path, query] = to.split("?");
    if (location.pathname !== path) return false;
    return query ? location.search.includes(query) : !location.search.includes("fav=1");
  };
  const doLogout = async () => { await logout(); navigate("/login"); };

  return (
    <header className="sticky top-0 z-40 backdrop-blur-md bg-white/90 dark:bg-gray-900/90 border-b border-border">
      <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between gap-3">
        <Link to="/catalogo" className="flex items-center gap-3 min-w-0" data-testid="header-logo-link">
          {settings.logo_url && <img src={settings.logo_url} alt="logo" className="h-10 w-10 object-contain" />}
          <span className="font-heading font-bold text-lg truncate" data-testid="platform-name">{settings.platform_name}</span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {user && links.map((l) => (
            <Link key={l.to} to={l.to} data-testid={l.testid}
              className={`relative flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors ${isActive(l.to) ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}>
              <l.icon size={16} /> {l.label}
              {l.badge > 0 && <span className="absolute -top-1 -right-1 bg-amber-600 text-white text-[10px] rounded-full h-5 min-w-5 px-1 flex items-center justify-center font-bold" data-testid={l.badgeTestid}>{l.badge}</span>}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <AccessCountdown />
          <LangToggle />
          <button onClick={theme.toggle} data-testid="theme-toggle-button"
            className="p-2 rounded-full hover:bg-muted transition-colors" aria-label="Cambiar tema">
            {theme.dark ? <Sun size={18} /> : <Moon size={18} />}
          </button>
          {user && (
            <button onClick={doLogout} data-testid="logout-button"
              className="hidden md:flex items-center gap-2 px-3 py-2 rounded-full text-sm hover:bg-muted transition-colors">
              <LogOut size={16} /> {t("header.logout")}
            </button>
          )}
          {user && (
            <button onClick={() => setOpen(!open)} data-testid="mobile-menu-button"
              className="md:hidden p-2 rounded-full hover:bg-muted transition-colors" aria-label="Menú">
              {open ? <X size={20} /> : <Menu size={20} />}
            </button>
          )}
        </div>
      </div>

      {open && user && (
        <div className="md:hidden border-t border-border bg-background px-4 py-3 space-y-1" data-testid="mobile-menu-drawer">
          {links.map((l) => (
            <Link key={l.to} to={l.to} onClick={() => setOpen(false)} data-testid={`mobile-${l.testid}`}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium ${isActive(l.to) ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}>
              <l.icon size={18} /> {l.label}
              {l.badge > 0 && <span className="ml-auto bg-amber-600 text-white text-xs rounded-full h-5 min-w-5 px-1 flex items-center justify-center font-bold">{l.badge}</span>}
            </Link>
          ))}
          <button onClick={doLogout} data-testid="mobile-logout-button"
            className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm w-full hover:bg-muted">
            <LogOut size={18} /> {t("header.logoutMobile")}
          </button>
        </div>
      )}
    </header>
  );
}
